import React, { useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Paper,
  CircularProgress,
  TextField,
  MenuItem,
  FormControlLabel,
  Switch,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import ArrowBack from '@mui/icons-material/ArrowBack';
import { useQuery, useMutation } from '@apollo/client/react';
import { useTranslation } from 'react-i18next';
import {
  GetOrganizationAddressDocument,
  UpdateOrganizationAddressDocument,
  DeleteOrganizationAddressDocument,
  type GetOrganizationAddressQuery,
  type AddressType,
  type UpdateOrganizationAddressMutation,
  type UpdateOrganizationAddressMutationVariables,
  type DeleteOrganizationAddressMutation,
  type DeleteOrganizationAddressMutationVariables,
} from '@/generated/root-types';
import { setOverrideTenantId } from '@gastrosoft/shared/utils';
import { useAuth } from '@/app/auth/AuthContext';
import { isSuperAdmin } from '@/utils/roles';
import { useNotification } from '@/app/contexts/NotificationContext';
import { errorsFromPayload } from '@/utils/mutationErrors';
import { translateAddressType } from '@/utils/addressLabels';
import {
  detailBackButtonSx,
  detailFieldGridSx,
  detailFieldSx,
  detailFullRowSx,
  detailHeaderRowSx,
  detailPageContainerSx,
  detailTitleSx,
} from '@/utils/detailPageLayout';

const ADDRESS_TYPES: AddressType[] = ['HEADQUARTERS', 'BRANCH', 'INVOICE', 'DELIVERY', 'OTHER'];

type AddressesListLocationState = { addressesListSearch?: string; tenantIdOverride?: string };

export default function OrganizationAddressDetail() {
  const { t, i18n } = useTranslation();
  const locale = i18n.language === 'de' ? 'de-DE' : 'en-US';
  const formatDateTime = (iso: string) => new Date(iso).toLocaleString(locale);
  const decStr = (v: unknown) => (v != null && v !== '' ? String(v) : '–');
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const addressesListPath = useMemo(() => {
    const raw = (location.state as AddressesListLocationState | null)?.addressesListSearch ?? '';
    if (!raw || raw === '?') return { pathname: '/organization-addresses' as const };
    return {
      pathname: '/organization-addresses' as const,
      search: raw.startsWith('?') ? raw : `?${raw}`,
    };
  }, [location.state]);
  const { user } = useAuth();
  const { showSuccess, showError } = useNotification();
  const superAdmin = isSuperAdmin(user);

  const [type, setType] = useState<AddressType>('INVOICE');
  const [isDefault, setIsDefault] = useState(false);
  const [line1, setLine1] = useState('');
  const [line2, setLine2] = useState('');
  const [city, setCity] = useState('');
  const [zip, setZip] = useState('');
  const [country, setCountry] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [deleteOpen, setDeleteOpen] = useState(false);

  const { data, loading, error, refetch } = useQuery<GetOrganizationAddressQuery>(
    GetOrganizationAddressDocument,
    {
      variables: { id: id! },
      skip: !id,
    },
  );

  const addr = data?.organizationAddress;

  const navTenantId = (location.state as AddressesListLocationState | null)?.tenantIdOverride;

  /** Parent pages unmount first and clear override; restore from navigation before the address query runs. */
  useLayoutEffect(() => {
    if (navTenantId) setOverrideTenantId(navTenantId);
  }, [navTenantId]);

  useEffect(() => {
    if (addr?.tenantId) setOverrideTenantId(addr.tenantId);
  }, [addr?.tenantId]);

  useEffect(() => {
    return () => setOverrideTenantId(null);
  }, []);

  useEffect(() => {
    if (loading || error || !id) return;
    if (data?.organizationAddress === null) {
      navigate(addressesListPath, { replace: true });
    }
  }, [loading, error, id, navigate, data?.organizationAddress, addressesListPath]);

  useEffect(() => {
    if (!addr) return;
    setType(addr.type);
    setIsDefault(addr.isDefault);
    setLine1(addr.addressLine1 ?? '');
    setLine2(addr.addressLine2 ?? '');
    setCity(addr.city ?? '');
    setZip(addr.zipCode ?? '');
    setCountry(addr.country ?? '');
    setPhone(addr.phoneNumber ?? '');
    setEmail(addr.email ?? '');
  }, [addr]);

  const [updateAddr, { loading: saving }] = useMutation<
    UpdateOrganizationAddressMutation,
    UpdateOrganizationAddressMutationVariables
  >(UpdateOrganizationAddressDocument);

  const [deleteAddr, { loading: deleting }] = useMutation<
    DeleteOrganizationAddressMutation,
    DeleteOrganizationAddressMutationVariables
  >(DeleteOrganizationAddressDocument);

  const handleSave = async () => {
    if (!id) return;
    try {
      const res = await updateAddr({
        variables: {
          input: {
            id,
            type,
            isDefault,
            addressLine1: line1 || undefined,
            addressLine2: line2 || undefined,
            city: city || undefined,
            zipCode: zip || undefined,
            country: country || undefined,
            phoneNumber: phone || undefined,
            email: email || undefined,
          },
        },
      });
      const top = (res as { errors?: ReadonlyArray<{ message?: string }> }).errors;
      if (top?.length) {
        showError(top.map((e) => e.message).join(' '));
        return;
      }
      const pe = errorsFromPayload(res.data?.updateOrganizationAddress);
      if (pe) {
        showError(pe);
        return;
      }
      showSuccess(t('common.saved'));
      await refetch();
    } catch (e) {
      showError(e instanceof Error ? e.message : t('errors.generic'));
    }
  };

  const handleDelete = async () => {
    if (!id) return;
    try {
      const res = await deleteAddr({ variables: { input: { id } } });
      const top = (res as { errors?: ReadonlyArray<{ message?: string }> }).errors;
      if (top?.length) {
        showError(top.map((e) => e.message).join(' '));
        return;
      }
      const pe = errorsFromPayload(res.data?.deleteOrganizationAddress);
      if (pe || !res.data?.deleteOrganizationAddress?.boolean) {
        showError(pe || t('errors.generic'));
        return;
      }
      showSuccess(t('common.deleted'));
      navigate(addressesListPath);
    } catch (e) {
      showError(e instanceof Error ? e.message : t('errors.generic'));
    }
  };

  if (loading) {
    return (
      <Box sx={{ ...detailPageContainerSx, display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={detailPageContainerSx}>
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate(addressesListPath)}
          sx={{ mb: 2 }}
        >
          {t('common.back')}
        </Button>
        <Typography color="error">{error.message}</Typography>
      </Box>
    );
  }

  if (!addr) {
    return null;
  }

  return (
    <Box sx={detailPageContainerSx}>
      <Box sx={detailHeaderRowSx}>
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate(addressesListPath)}
          sx={detailBackButtonSx}
        >
          {t('common.back')}
        </Button>
        <Typography variant="h4" component="h1" sx={detailTitleSx}>
          {t('orgAddresses.title')}
        </Typography>
      </Box>

      <Paper
        variant="outlined"
        sx={{ p: { xs: 1.5, sm: 2 }, width: '100%', boxSizing: 'border-box' }}
      >
        <Box sx={detailFieldGridSx}>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ ...detailFullRowSx, fontWeight: 600 }}
          >
            {addr.organization?.name}
          </Typography>

          <TextField
            sx={detailFieldSx}
            label={t('common.tenant')}
            value={addr.tenant?.name ?? '–'}
            fullWidth
            InputProps={{ readOnly: true }}
          />
          <TextField
            sx={detailFieldSx}
            label={t('common.created')}
            value={formatDateTime(addr.createdAt)}
            fullWidth
            InputProps={{ readOnly: true }}
          />
          <TextField
            sx={detailFieldSx}
            label={t('common.updated')}
            value={formatDateTime(addr.updatedAt)}
            fullWidth
            InputProps={{ readOnly: true }}
          />

          <TextField
            sx={{ ...detailFieldSx, ...detailFullRowSx }}
            label={t('orgAddresses.addressDescription')}
            value={addr.description ?? '–'}
            fullWidth
            multiline
            minRows={2}
            InputProps={{ readOnly: true }}
            helperText={t('masterData.apiFieldReadOnly')}
          />
          <TextField
            sx={detailFieldSx}
            label={t('orgAddresses.latitude')}
            value={decStr(addr.latitude)}
            fullWidth
            InputProps={{ readOnly: true }}
          />
          <TextField
            sx={detailFieldSx}
            label={t('orgAddresses.longitude')}
            value={decStr(addr.longitude)}
            fullWidth
            InputProps={{ readOnly: true }}
          />
          <TextField
            sx={detailFieldSx}
            label={t('orgAddresses.status')}
            value={addr.status ?? '–'}
            fullWidth
            InputProps={{ readOnly: true }}
          />

          <TextField
            sx={detailFieldSx}
            select
            label={t('common.type')}
            value={type}
            onChange={(e) => setType(e.target.value as AddressType)}
            fullWidth
          >
            {ADDRESS_TYPES.map((x) => (
              <MenuItem key={x} value={x}>
                {translateAddressType(t, x)}
              </MenuItem>
            ))}
          </TextField>
          <FormControlLabel
            sx={{ ...detailFullRowSx, alignSelf: 'center', ml: 0 }}
            control={
              <Switch checked={isDefault} onChange={(e) => setIsDefault(e.target.checked)} />
            }
            label={t('orgAddresses.standardAddress')}
          />

          <TextField
            sx={{ ...detailFieldSx, ...detailFullRowSx }}
            label={t('orgAddresses.line1')}
            value={line1}
            onChange={(e) => setLine1(e.target.value)}
            fullWidth
          />
          <TextField
            sx={{ ...detailFieldSx, ...detailFullRowSx }}
            label={t('orgAddresses.line2')}
            value={line2}
            onChange={(e) => setLine2(e.target.value)}
            fullWidth
          />
          <TextField
            sx={detailFieldSx}
            label={t('orgAddresses.city')}
            value={city}
            onChange={(e) => setCity(e.target.value)}
            fullWidth
          />
          <TextField
            sx={detailFieldSx}
            label={t('orgAddresses.zip')}
            value={zip}
            onChange={(e) => setZip(e.target.value)}
            fullWidth
          />
          <TextField
            sx={detailFieldSx}
            label={t('orgAddresses.country')}
            value={country}
            onChange={(e) => setCountry(e.target.value)}
            fullWidth
          />
          <TextField
            sx={detailFieldSx}
            label={t('organizationDetail.phone')}
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            fullWidth
          />
          <TextField
            sx={detailFieldSx}
            label={t('organizationDetail.email')}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            fullWidth
          />

          <Box sx={{ ...detailFullRowSx, display: 'flex', gap: 1, flexWrap: 'wrap', pt: 0.5 }}>
            <Button variant="contained" onClick={handleSave} disabled={saving}>
              {t('common.save')}
            </Button>
            {superAdmin && (
              <Button color="error" onClick={() => setDeleteOpen(true)}>
                {t('common.delete')}
              </Button>
            )}
          </Box>
        </Box>
      </Paper>

      <Dialog open={deleteOpen} onClose={() => setDeleteOpen(false)}>
        <DialogTitle>{t('orgAddresses.confirmDelete')}</DialogTitle>
        <DialogContent>
          <Typography>{t('orgAddresses.confirmDeleteBody')}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteOpen(false)}>{t('common.cancel')}</Button>
          <Button color="error" onClick={handleDelete} disabled={deleting}>
            {t('common.delete')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
